import React, { useState, useEffect, useCallback, useMemo } from "react";
import { X, ExternalLink, CheckCircle2, XCircle, Pencil, AlertTriangle, ListChecks, Loader2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { VISIT_TASK_TITLES } from "@/lib/visitTaskMapping";

/** Format an ISO datetime as e.g. "Wed 5 Aug 2026 · 10:00–11:00". */
function formatWindow(startISO, endISO) {
    if (!startISO) return "";
    const s = new Date(startISO);
    const e = endISO ? new Date(endISO) : null;
    const dateStr = s.toLocaleDateString(undefined, {
        weekday: "short", day: "numeric", month: "short", year: "numeric",
    });
    const t = (d) => d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    return e ? `${dateStr} · ${t(s)}–${t(e)}` : `${dateStr} · ${t(s)}`;
}

const STATUS_COLOURS = {
    scheduled:   { bg: "#EEF2F6", fg: "#131B2F", label: "Scheduled" },
    confirmed:   { bg: "#DBEAFE", fg: "#1E40AF", label: "Confirmed" },
    in_progress: { bg: "#FEF3C7", fg: "#92400E", label: "In progress" },
    completed:   { bg: "#DCFCE7", fg: "#166534", label: "Completed" },
    cancelled:   { bg: "#FEE2E2", fg: "#991B1B", label: "Cancelled" },
};

// VISIT-TASK-001D · Canonical task-status labels — never leak the
// raw persistence string (e.g. "not_started") into the required-
// tasks pill. Ad-hoc tasks already ship Title-case values which
// pass through unchanged.
const CANONICAL_TASK_STATUS_LABELS = {
    not_started: "Not started",
    in_progress: "In progress",
    blocked:     "Blocked",
    complete:    "Complete",
    cancelled:   "Cancelled",
};

function formatTaskStatus(raw) {
    if (!raw) return "Open";
    const key = String(raw).toLowerCase();
    if (CANONICAL_TASK_STATUS_LABELS[key]) return CANONICAL_TASK_STATUS_LABELS[key];
    // Ad-hoc statuses ship Title-case (Not Started, In Progress, etc.) —
    // pass through when the display string doesn't need normalising.
    return raw;
}

/**
 * CalendarAppointmentDetail — right-side drawer shown when an event is clicked.
 * Provides Edit, Cancel-with-reason, Mark-complete and Open-project actions.
 */
export default function CalendarAppointmentDetail({
    open, appointment, engineers, onClose, onEdit, onRefresh,
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const role = (user?.role || "").toLowerCase();
    // DEV-020 UAT correction 2 · Preserve the caller's exact Calendar
    // state (date/view/query) so Back from Focus returns to the same
    // slice they were looking at. Falls back to a friendly role-aware
    // calendar default when we're not currently on a calendar URL.
    const returnTo = useMemo(() => {
        if (location.pathname && location.pathname.includes("calendar")) {
            return `${location.pathname}${location.search}`;
        }
        return role === "operations" ? "/operations/calendar" : "/installer/calendar";
    }, [location.pathname, location.search, role]);
    const [cancelling, setCancelling] = useState(false);
    const [reason, setReason] = useState("");
    const [showCancelForm, setShowCancelForm] = useState(false);
    // VISIT-TASK-001 · Linked-task state — loading / links / error.
    // Refreshed every time the appointment or its status changes so
    // completing a linked canonical task and reopening the drawer
    // reflects the up-to-date status.
    const [tasksLoading, setTasksLoading] = useState(false);
    const [tasksError, setTasksError] = useState("");
    const [linkedTasks, setLinkedTasks] = useState(null);

    const loadTasks = useCallback(async (id) => {
        if (!id) return;
        setTasksLoading(true);
        setTasksError("");
        try {
            const r = await api.calendarAppointmentTasks(id);
            setLinkedTasks(r?.links || []);
        } catch (e) {
            setLinkedTasks(null);
            setTasksError(formatApiError(e?.response?.data?.detail) || "Failed to load linked tasks.");
        } finally { setTasksLoading(false); }
    }, []);

    useEffect(() => {
        if (!open || !appointment?.id) return;
        loadTasks(appointment.id);
    }, [open, appointment?.id, appointment?.status, appointment?.updated_at, loadTasks]);

    // VISIT-TASK-001B · Role-aware navigation.
    // The backend returns installer-shaped ``navigation`` (which
    // hits ``/installer/project/:id``). That route is guarded by
    // ``ProtectedRoute role="installer"`` and bounces Ops users
    // back to ``/operations``. Build the correct URL per role
    // client-side so both authorised surfaces open the exact task.
    //
    // DEV-020 UAT correction 2 · For Ops/Sales, drop the legacy
    // ``?project=<id>`` selector and route through the DEV-020
    // Single Project Focus URL (`/operations/projects/:id` or
    // `/sales/projects/:id`). Task/appointment context survives
    // via querystring, and `returnTo` in `location.state` restores
    // the exact Calendar slice on Back.
    const openLinkedTask = useCallback((link) => {
        if (!link) return;
        const pid = link.project_id || appointment?.project_id;
        const tid = link.task_id;
        const appt = link.appointment_id || appointment?.id;
        if (!pid || !tid) return;
        const qs = new URLSearchParams();
        qs.set("taskId", tid);
        if (link.task_ref_kind === "ad_hoc") qs.set("taskKind", "adhoc");
        if (appt) qs.set("appointmentId", appt);
        if (role === "operations") {
            navigate(
                `/operations/projects/${encodeURIComponent(pid)}?${qs.toString()}`,
                { state: { returnTo } },
            );
            return;
        }
        if (role === "sales") {
            navigate(
                `/sales/projects/${encodeURIComponent(pid)}?${qs.toString()}`,
                { state: { returnTo } },
            );
            return;
        }
        // Installer / lead / customer fallback — use the
        // server-provided navigation string when present, else
        // build the installer URL directly.
        if (link.navigation) {
            navigate(link.navigation);
            return;
        }
        navigate(`/installer/project/${pid}?${qs.toString()}`);
    }, [navigate, role, appointment?.project_id, appointment?.id, returnTo]);

    const openProject = useCallback(() => {
        const pid = appointment?.project_id;
        if (!pid) return;
        // DEV-020 UAT correction 1 · Open project must go to the DEV-020
        // Single Project Focus route (role-aware). Never fall back to
        // the legacy master-detail split view or the installer route
        // for Ops/Sales callers. `returnTo` restores the exact
        // Calendar slice on Back (DEV-020 UAT correction 2).
        if (role === "operations") {
            navigate(
                `/operations/projects/${encodeURIComponent(pid)}`,
                { state: { returnTo } },
            );
            return;
        }
        if (role === "sales") {
            navigate(
                `/sales/projects/${encodeURIComponent(pid)}`,
                { state: { returnTo } },
            );
            return;
        }
        navigate(`/installer/project/${pid}`);
    }, [navigate, role, appointment?.project_id, returnTo]);

    if (!open || !appointment) return null;

    const engMap = Object.fromEntries((engineers || []).map((e) => [e.id, e]));
    const assigned = (appointment.engineer_ids || []).map((id) => engMap[id]).filter(Boolean);
    const st = STATUS_COLOURS[appointment.status] || STATUS_COLOURS.scheduled;

    async function doCancel() {
        setCancelling(true);
        try {
            await api.cancelCalendarAppointment(appointment.id, reason);
            toast.success("Appointment cancelled");
            onRefresh?.();
            onClose?.();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to cancel");
        } finally { setCancelling(false); }
    }

    async function doComplete() {
        try {
            await api.completeCalendarAppointment(appointment.id);
            toast.success("Appointment completed");
            onRefresh?.();
            onClose?.();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to update");
        }
    }

    return (
        <div className="fixed inset-0 z-50" data-testid="cal-detail-drawer">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <aside
                className="absolute right-0 top-0 bottom-0 w-full sm:max-w-md bg-white shadow-2xl flex flex-col"
                role="dialog"
                aria-labelledby="detail-title"
            >
                <div className="px-5 py-4 border-b border-[#E5E7EB] flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <span
                                className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium"
                                style={{ background: st.bg, color: st.fg }}
                                data-testid="cal-detail-status"
                            >
                                {st.label}
                            </span>
                            {appointment.clash_override && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#FEE2E2] text-[#991B1B]" title="Clash override recorded">
                                    <AlertTriangle size={11} strokeWidth={1.75} /> Clash overridden
                                </span>
                            )}
                        </div>
                        <h3 id="detail-title" className="text-lg font-semibold text-[#131B2F] truncate">
                            {appointment.title || appointment.appointment_type}
                        </h3>
                        <div className="text-sm text-[#4B5565] mt-1">
                            {appointment.appointment_type} · {formatWindow(appointment.start_at, appointment.end_at)}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F5F5F7] text-[#4B5565]"
                        aria-label="Close"
                        data-testid="cal-detail-close"
                    >
                        <X size={18} strokeWidth={1.75} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                    {/* Customer + Project */}
                    <div>
                        <div className="text-xs uppercase tracking-[0.08em] text-[#4B5565] mb-1">Customer</div>
                        <div className="text-sm font-medium text-[#131B2F]">
                            {appointment.customer_name || "—"}
                        </div>
                        {appointment.project_address && (
                            <div className="text-xs text-[#4B5565] mt-0.5">{appointment.project_address}</div>
                        )}
                        <button
                            onClick={openProject}
                            data-testid="cal-detail-open-project"
                            className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#3E9E2E] hover:underline"
                        >
                            <ExternalLink size={14} strokeWidth={1.75} /> Open project
                        </button>
                    </div>

                    {/* Engineers */}
                    <div>
                        <div className="text-xs uppercase tracking-[0.08em] text-[#4B5565] mb-1">Engineers</div>
                        {assigned.length === 0 ? (
                            <div className="text-sm text-[#4B5565] italic">Unassigned</div>
                        ) : (
                            <div className="flex flex-wrap gap-1.5">
                                {assigned.map((e) => (
                                    <span
                                        key={e.id}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-white"
                                        style={{ background: e.colour || "#3E9E2E" }}
                                        data-testid={`cal-detail-engineer-${e.id}`}
                                    >
                                        {e.displayName}
                                        {e.active === false && <span className="text-[10px] opacity-90">(archived)</span>}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Location */}
                    {appointment.location && (
                        <div>
                            <div className="text-xs uppercase tracking-[0.08em] text-[#4B5565] mb-1">Location</div>
                            <div className="text-sm text-[#131B2F]">{appointment.location}</div>
                        </div>
                    )}

                    {/* VISIT-TASK-001 · Required linked tasks. Loading,
                        empty and error states are all distinct so a
                        missing/failed load never implies the visit is
                        complete. Navigation routes to the existing
                        authorised task surface returned by the API. */}
                    <div data-testid="cal-detail-linked-tasks-section">
                        <div className="flex items-center gap-2 mb-2">
                            <ListChecks size={14} strokeWidth={1.75} className="text-[#4B5565]" />
                            <div className="text-xs uppercase tracking-[0.08em] text-[#4B5565]">Required tasks</div>
                            <button
                                type="button"
                                onClick={() => loadTasks(appointment.id)}
                                className="ml-auto text-xs text-[#3E9E2E] hover:underline"
                                data-testid="cal-detail-linked-tasks-refresh"
                            >
                                Refresh
                            </button>
                        </div>
                        {tasksLoading && (
                            <div className="flex items-center gap-2 text-xs text-[#4B5565]" data-testid="cal-detail-linked-tasks-loading">
                                <Loader2 size={12} className="animate-spin" /> Loading required tasks…
                            </div>
                        )}
                        {!tasksLoading && tasksError && (
                            <div className="text-xs text-[#B91C1C]" data-testid="cal-detail-linked-tasks-error">
                                {tasksError} Refresh to try again.
                            </div>
                        )}
                        {!tasksLoading && !tasksError && Array.isArray(linkedTasks) && linkedTasks.length === 0 && (
                            <div className="text-xs text-[#4B5565] italic" data-testid="cal-detail-linked-tasks-empty">
                                No required tasks are currently linked to this visit.
                            </div>
                        )}
                        {!tasksLoading && !tasksError && Array.isArray(linkedTasks) && linkedTasks.length > 0 && (
                            <ul className="space-y-1.5" data-testid="cal-detail-linked-tasks-list">
                                {linkedTasks.map((t) => {
                                    const isComplete = t.status === "complete" || t.status === "Completed" || t.status === "Complete";
                                    const isCancelled = t.status === "Cancelled" || t.status === "cancelled";
                                    const label = t.title
                                        || VISIT_TASK_TITLES[t.task_key || ""]
                                        || t.task_key
                                        || t.description
                                        || "Untitled task";
                                    return (
                                        <li
                                            key={t.id}
                                            data-testid={`cal-detail-linked-task-${t.task_id}`}
                                            className="flex items-start gap-2 text-sm"
                                        >
                                            <span
                                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                                    isComplete
                                                        ? "bg-[#DCFCE7] text-[#166534]"
                                                        : isCancelled
                                                            ? "bg-[#FEE2E2] text-[#991B1B]"
                                                            : "bg-[#EEF2F6] text-[#131B2F]"
                                                }`}
                                                data-testid={`cal-detail-linked-task-status-${t.task_id}`}
                                            >
                                                {isComplete ? "Complete" : isCancelled ? "Cancelled" : formatTaskStatus(t.status)}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[#131B2F] truncate">{label}</div>
                                                {t.required === false && (
                                                    <div className="text-[10px] text-[#4B5565]">Optional</div>
                                                )}
                                            </div>
                                            {(t.navigation || t.task_id) && (
                                                <button
                                                    type="button"
                                                    onClick={() => openLinkedTask(t)}
                                                    className="text-xs text-[#3E9E2E] hover:underline"
                                                    data-testid={`cal-detail-linked-task-open-${t.task_id}`}
                                                >
                                                    Open
                                                </button>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    {/* Notes */}
                    {appointment.notes && (
                        <div>
                            <div className="text-xs uppercase tracking-[0.08em] text-[#4B5565] mb-1">Notes</div>
                            <div className="text-sm text-[#131B2F] whitespace-pre-wrap">{appointment.notes}</div>
                        </div>
                    )}

                    {appointment.status === "cancelled" && appointment.cancellation_reason && (
                        <div>
                            <div className="text-xs uppercase tracking-[0.08em] text-[#DA3535] mb-1">Cancellation reason</div>
                            <div className="text-sm text-[#131B2F]">{appointment.cancellation_reason}</div>
                        </div>
                    )}

                    {/* Cancel form */}
                    {showCancelForm && (
                        <div className="border border-[#DA3535]/30 rounded-lg p-3 bg-[#FEF3F3]">
                            <div className="text-sm font-medium text-[#131B2F] mb-2">Cancel this appointment?</div>
                            <textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Reason (optional)"
                                rows={2}
                                data-testid="cal-cancel-reason"
                                className="w-full px-2.5 py-2 border border-[#D1D5DB] rounded-md text-sm bg-white"
                            />
                            <div className="mt-2 flex gap-2">
                                <button
                                    onClick={() => setShowCancelForm(false)}
                                    className="px-3 py-1.5 text-sm rounded-md border border-[#D1D5DB]"
                                >
                                    Keep it
                                </button>
                                <button
                                    onClick={doCancel}
                                    disabled={cancelling}
                                    className="px-3 py-1.5 text-sm rounded-md bg-[#DA3535] text-white disabled:opacity-60"
                                    data-testid="cal-cancel-confirm"
                                >
                                    {cancelling ? "Cancelling…" : "Confirm cancel"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-5 py-4 border-t border-[#E5E7EB] flex flex-wrap gap-2 justify-end">
                    <button
                        onClick={onEdit}
                        data-testid="cal-detail-edit"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#D1D5DB] text-sm font-medium hover:bg-[#F5F5F7]"
                    >
                        <Pencil size={14} strokeWidth={1.75} /> Edit
                    </button>
                    {appointment.status !== "completed" && appointment.status !== "cancelled" && (
                        <>
                            <button
                                onClick={doComplete}
                                data-testid="cal-detail-complete"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#3E9E2E]/50 text-[#166534] bg-[#DCFCE7] text-sm font-medium hover:bg-[#BBF7D0]"
                            >
                                <CheckCircle2 size={14} strokeWidth={1.75} /> Complete
                            </button>
                            <button
                                onClick={() => setShowCancelForm(true)}
                                data-testid="cal-detail-cancel-open"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#DA3535]/40 text-[#991B1B] bg-[#FEE2E2] text-sm font-medium hover:bg-[#FECACA]"
                            >
                                <XCircle size={14} strokeWidth={1.75} /> Cancel
                            </button>
                        </>
                    )}
                </div>
            </aside>
        </div>
    );
}
